import React, { FC, memo, useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import Directions from "Components/Directions";
import { setView } from "Containers/CampusMap";
import {
  resetPathStops,
  setPathStops,
  setStopState,
  StopState,
} from "Containers/Stops";
import { areEqual, areEqualShallow } from "Utils/areEqual";

import { setStartingPoint, setDestination } from "./DirectionsActions";
import { calculateDirections } from "../../map/coordinates_v3";

interface Props {
  showSwitcher: boolean;
  trailsCount: number;
  locationBounds: {
    minLat: number;
    maxLat: number;
    minLng: number;
    maxLng: number;
  };
}

interface PositionProps {
  latitude: number;
  longitude: number;
}

const DirectionsContainer: FC<Props> = memo(
  ({ showSwitcher, trailsCount, locationBounds }: Props) => {
    const dispatch = useDispatch();
    const [currentPosition, setCurrentPosition] = useState(null);
    const [instructions, setInstructions] = useState<string[]>([]);

    // This is used for the click events in the search options icons to display the proper view
    const setSearchOptionsView = useCallback(
      (option: string) => {
        dispatch(setView(option));
      },
      [dispatch]
    );

    const { startingPoint, destination } = useSelector(
      (selectors: any) => selectors.directions,
      areEqual
    );

    let stops = useSelector(
      ({ stops: stopsList }: any) =>
        stopsList.filter((stop: any) => !stop.isPointOfInterest),
      areEqual
    );
    const view = useSelector((store: any) => store.searchOptionsView.view);

    const inBounds = (lat, lng) => {
      if (locationBounds !== null) {
        return (
          lat < locationBounds.minLat &&
          lng > locationBounds.minLng &&
          lat > locationBounds.maxLat &&
          lng < locationBounds.maxLng
        );
      }
      return false;
    };

    if (navigator?.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (inBounds(position.coords.latitude, position.coords.longitude)) {
            setCurrentPosition({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
          }
        },
        () => {},
        {
          maximumAge: 0,
          timeout: 10000,
          enableHighAccuracy: true,
        }
      );
    }

    /* Add Current Location at the top of the list if it's within the location bounds */
    if (currentPosition !== null) {
      stops = [
        {
          id: -1,
          realLat: currentPosition.latitude,
          realLng: currentPosition.longitude,
          customLat: 0,
          customLng: 0,
          title: "Current Location",
        },
        ...stops,
      ];
    }

    const mapSwitcher = useSelector((store: any) => store.mapSwitcher);

    const setStartingPointSuggestion = (point: any) => {
      dispatch(setStartingPoint(point));
    };
    const setDestinationSuggestion = (point: any) => {
      dispatch(setDestination(point));
    };

    const [showDirections, setShowDirections] = useState(true);

    useEffect(() => {
      // If there is a starting point and destination, then dispatch and render on the map.
      if (startingPoint?.title?.length && destination?.title?.length) {
        // Dispath the starting point and destination, to Pathstops.  THis will render the directions on the map
        const {
          realLat: startPointLat,
          realLng: startPointLng,
          customLat: startCustomLat,
          customLng: startCustomLng,
        } = startingPoint;
        const {
          realLat: destPointLat,
          realLng: destPointLng,
          customLat: destCustomLat,
          customLng: destCustomLng,
        } = destination;

        dispatch(
          setStopState([startingPoint.id, destination.id], StopState.Selected)
        );
        dispatch(
          setPathStops([
            {
              realLat: startPointLat,
              realLng: startPointLng,
              customLat: startCustomLat,
              customLng: startCustomLng,
              title: startingPoint.title,
              id: startingPoint.id,
            },
            {
              realLat: destPointLat,
              realLng: destPointLng,
              customLat: destCustomLat,
              customLng: destCustomLng,
              title: destination.title,
              id: destination.id,
            },
          ])
        );
        const pathCb = (paths: any, steps: any) => setInstructions(steps);
        const startLatLng = new window.google.maps.LatLng(
          startPointLat,
          startPointLng
        );
        const endLatLng = new window.google.maps.LatLng(
          destPointLat,
          destPointLng
        );
        calculateDirections(startLatLng, [], endLatLng, pathCb, pathCb);
      }
      return () => {
        // Reset pathStops to an empty array, in Redux, when the component is unmounted
        dispatch(resetPathStops());
        setInstructions([]);
      };
    }, [dispatch, startingPoint, destination]);

    useEffect(() => {
      const directionsContainer = document.getElementById("searchOptionsTitle");
      if (view === "directions" && directionsContainer) {
        directionsContainer.focus();
      }
    }, [view]);

    return (
      <Directions
        suggestions={stops}
        startingPoint={startingPoint}
        destination={destination}
        setStartingPoint={setStartingPointSuggestion}
        setDestination={setDestinationSuggestion}
        setSearchOptionsView={setSearchOptionsView}
        showDirections={showDirections}
        setShowDirections={setShowDirections}
        mapSwitcher={mapSwitcher}
        showSwitcher={showSwitcher}
        trailsCount={trailsCount}
        instructions={instructions}
      />
    );
  },
  areEqualShallow
);

export default DirectionsContainer;
